// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { User } from "@swiggy-private/payment-core";
import { format, Tick } from "@swiggy-private/core-ui";

// interfaces
import { IPayLaterPaymentGroup, PayLaterPaymentMethodData, PayLaterLinkedData } from "@payment-ui/interfaces/PayLater";

// components
import CircleLoader from "@payment-ui/widgets/circle-loader";
import { LinkAccount } from "@payment-ui/components/link-account";
import { TnC } from "./Tnc";
import { MetaPoints } from "./MetaPoints";

// constants
import { TEXTS as PAYLATER_TEXTS } from "@payment-ui/constants/PayLater";

// helpers
import { getPayLaterLinkedMetaText, getTNCUrl } from "@payment-ui/helpers/PayLater";
import { getPaymentMethodDisplayName } from "@payment-ui/helpers/Payment";

// test-ids
import { PayLaterTestIds } from "@swiggy-private/payment-test-ids";

import styles from "../PayLater.v4.scss";

interface LinkStatusProps {
    loading: boolean;
    selected: boolean;
    methodData: PayLaterPaymentMethodData;
    payLaterLinkedData?: PayLaterLinkedData;
    linkAccountActions: IPayLaterPaymentGroup["paymentActions"]["linkAccountActions"];
    renderIcon: (width?: number) => ReactElement;
    closeDialog: VoidFunction;
    canShowDialog: boolean;
    user: User;
    payLaterTestIds: PayLaterTestIds;
}

interface DialogHeaderParams {
    renderIcon: LinkStatusProps["renderIcon"];
    payLaterTestIds: PayLaterTestIds;
    payLaterLinkedData?: PayLaterLinkedData;
}

interface DialogContentParams {
    methodData: PayLaterPaymentMethodData;
    user: User;
    payLaterTestIds: PayLaterTestIds;
    payLaterLinkedData?: PayLaterLinkedData;
}

const _renderDialogHeader = ({ renderIcon, payLaterTestIds, payLaterLinkedData }: DialogHeaderParams): ReactElement => {
    return (
        <div className={styles.dialogHeader}>
            <div className={styles.dialogHeaderIcon} data-testid={payLaterTestIds.DIALOG_HEADER.ICON}>
                {renderIcon()}
            </div>
            {payLaterLinkedData && (
                <div className={styles.dialogHeaderText} data-testid={payLaterTestIds.DIALOG_HEADER.TEXT}>
                    {getPayLaterLinkedMetaText(payLaterLinkedData)}
                </div>
            )}
        </div>
    );
};

const _renderDialogContent = ({
    methodData,
    user,
    payLaterLinkedData,
    payLaterTestIds,
}: DialogContentParams): ReactElement => {
    const methodName = getPaymentMethodDisplayName(methodData);
    return (
        <div className={styles.dialogContent} data-testid={payLaterTestIds.DIALOG_CONTENT.WRAPPER}>
            {payLaterLinkedData && (
                <MetaPoints meta={payLaterLinkedData} dataTestId={payLaterTestIds.DIALOG_CONTENT.META_POINT} />
            )}
            <TnC tncUrl={getTNCUrl(methodData)} dataTestId={payLaterTestIds.DIALOG_CONTENT.TNC_LINK} />
            <div className={styles.dialogContentText} data-testid={payLaterTestIds.DIALOG_CONTENT.METHOD_NAME}>
                {format(PAYLATER_TEXTS.LINK_TEXT_V4, methodName)}
            </div>
            <div className={styles.dialogContentSubtext} data-testid={payLaterTestIds.DIALOG_CONTENT.EMAIL}>
                {user.email}
            </div>
            <div
                className={styles.dialogContentNumber}
                data-testid={payLaterTestIds.DIALOG_CONTENT.MOBILE}
            >{` ${user.mobile}`}</div>
        </div>
    );
};

function LinkStatus(props: LinkStatusProps): ReactElement {
    const {
        renderIcon,
        payLaterLinkedData,
        methodData,
        payLaterTestIds,
        linkAccountActions: { onProceed: proceedToLink },
        canShowDialog,
        closeDialog,
    } = props;

    const container = classNames({
        [styles.payLaterLinkAccount]: true,
        [styles.payLaterLinkAccountWithSubText]: methodData?.meta?.promoMsg,
    });

    const renderDialogHeader = useCallback(
        (): ReactElement => _renderDialogHeader({ renderIcon, payLaterLinkedData, payLaterTestIds }),
        [renderIcon, payLaterLinkedData, payLaterTestIds],
    );

    const renderDialogContent = useCallback(
        (user: User): ReactElement => _renderDialogContent({ methodData, user, payLaterLinkedData, payLaterTestIds }),
        [methodData, payLaterLinkedData, payLaterTestIds],
    );

    const linkAccountActions = useMemo(
        () => ({
            onProceed: (): void => {
                return proceedToLink(props.methodData.paymentCode, props.user?.mobile || "");
            },
        }),
        [proceedToLink, props.methodData.paymentCode, props.user],
    );

    const renderContent = (): ReactElement | null => {
        if (props.loading) {
            return (
                <CircleLoader
                    size="medium"
                    className={styles.payLaterLinkAccountLoader}
                    data-testid={payLaterTestIds.CIRCLE_LOADER}
                />
            );
        }

        if (!payLaterLinkedData) {
            return null;
        }

        if (payLaterLinkedData.linked) {
            return (
                <Tick
                    selected={props.selected}
                    className={styles.payLaterLinkAccountBalance}
                    data-testid={payLaterTestIds.TICK}
                />
            );
        }

        return (
            <div className={styles.payLaterLinkAccountContainer}>
                <LinkAccount
                    renderHeader={renderDialogHeader}
                    renderContent={renderDialogContent}
                    actions={linkAccountActions}
                    user={props.user}
                    data-testid={payLaterTestIds.LINK_ACCOUNT}
                    canShowDialog={canShowDialog}
                    closeDialog={closeDialog}
                />
            </div>
        );
    };

    return <div className={container}>{renderContent()}</div>;
}

export default memo(LinkStatus);
